/* eslint-disable react/prop-types */
"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import AppModalShell, {
  AppModalButton,
  AppModalCloseIcon,
  AppModalIconButton,
} from "../common/AppModalShell";
import EventToolsBackButton from "./EventToolsBackButton";
import EventToolsSkeleton, {
  EventToolsAnimatedState,
} from "./EventToolsSkeletons";

const MAX_DATES = 10;
const MAX_INTERVALS_PER_DATE = 2;
const MAX_OPTIONS = 20;
const pendingPollSaves = new Set();
const failedPollDrafts = new Map();
const POLL_SAVE_STATE_EVENT = "vg:concept-date-poll-save-state";

const updatePendingPollSave = (eventId, pending) => {
  const key = String(eventId);
  if (pending) pendingPollSaves.add(key);
  else pendingPollSaves.delete(key);
  window.dispatchEvent(new CustomEvent(POLL_SAVE_STATE_EVENT, { detail: { eventId: key, pending } }));
};

const pad2 = (value) => String(value).padStart(2, "0");

const getDefaultYearMonth = () => {
  const now = new Date();
  return {
    year: String(now.getFullYear()),
    month: pad2(now.getMonth() + 1),
  };
};

const normalizeDatePart = (raw, maxLength) =>
  String(raw ?? "")
    .replace(/\D/g, "")
    .slice(0, maxLength);

const parseIsoDate = (isoDate) => {
  const value = String(isoDate || "").trim();
  if (!value) return null;
  const [yearRaw, monthRaw, dayRaw] = value.split("-");
  const year = normalizeDatePart(yearRaw, 4);
  const month = normalizeDatePart(monthRaw, 2);
  const day = normalizeDatePart(dayRaw, 2);
  if (!year || !month || !day) return null;
  return { year, month, day };
};

const toIsoDate = (day) => {
  const yearRaw = normalizeDatePart(day?.year, 4);
  const monthRaw = normalizeDatePart(day?.month, 2);
  const dayRaw = normalizeDatePart(day?.day, 2);
  if (yearRaw.length !== 4 || !monthRaw || !dayRaw) return "";

  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const dayOfMonth = Number(dayRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(dayOfMonth)) {
    return "";
  }
  if (month < 1 || month > 12 || dayOfMonth < 1 || dayOfMonth > 31) return "";

  const utcDate = new Date(Date.UTC(year, month - 1, dayOfMonth));
  if (
    utcDate.getUTCFullYear() !== year ||
    utcDate.getUTCMonth() + 1 !== month ||
    utcDate.getUTCDate() !== dayOfMonth
  ) {
    return "";
  }

  return `${year}-${pad2(month)}-${pad2(dayOfMonth)}`;
};

const isFutureDate = (isoDate) => {
  const parts = parseIsoDate(isoDate);
  if (!parts) return false;
  const selected = new Date(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    0,
    0,
    0,
    0
  );
  if (Number.isNaN(selected.getTime())) return false;
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  return selected.getTime() > todayStart.getTime();
};

const getMinSelectableDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return `${tomorrow.getFullYear()}-${pad2(tomorrow.getMonth() + 1)}-${pad2(tomorrow.getDate())}`;
};

const formatDateForDisplay = (isoDate, language) => {
  const parts = parseIsoDate(isoDate);
  if (!parts) return "";

  const date = new Date(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    12,
    0,
    0,
    0
  );
  if (Number.isNaN(date.getTime())) return "";

  try {
    return new Intl.DateTimeFormat(language || undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  } catch (_error) {
    return isoDate;
  }
};

const makeDay = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  ...getDefaultYearMonth(),
  day: "",
  intervals: [],
});

const requireStartForDuration = (days, durationMinutes) => durationMinutes
  ? days.map((day) => ({
      ...day,
      intervals: day.intervals?.length ? day.intervals : [{ start: "", end: "" }],
    }))
  : days;

const normalizeTime = (raw) => {
  const value = String(raw || "").trim();
  if (!value) return "";
  return value.slice(0, 5);
};

const endTimeFromDuration = (start, durationMinutes) => {
  if (!/^\d{2}:\d{2}$/.test(start) || !durationMinutes) return "";
  const [hours, minutes] = start.split(":").map(Number);
  const endMinutes = hours * 60 + minutes + durationMinutes;
  if (hours > 23 || minutes > 59 || endMinutes >= 24 * 60) return "";
  return `${pad2(Math.floor(endMinutes / 60))}:${pad2(endMinutes % 60)}`;
};

const ConceptDatePollModal = ({
  visible = false,
  eventId = null,
  durationMinutes = null,
  onClose = () => {},
  onSaved = null,
  onBack = null,
  embedded = false,
  zIndexClassName = "z-[1200]",
}) => {
  const { t, i18n } = useTranslation("events");
  const parsedDuration = Number(durationMinutes);
  const autoDurationMinutes = Number.isInteger(parsedDuration) && parsedDuration > 0 && parsedDuration < 24 * 60
    ? parsedDuration : null;

  const [loading, setLoading] = React.useState(false);
  const [pollLoaded, setPollLoaded] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [localError, setLocalError] = React.useState("");
  const [days, setDays] = React.useState([makeDay()]);
  const [hasExistingPoll, setHasExistingPoll] = React.useState(false);

  const mapPollToDays = React.useCallback((pollPayload) => {
    const options = Array.isArray(pollPayload?.options) ? pollPayload.options : [];
    if (!options.length) return [makeDay()];

    const grouped = [];
    const byDate = new Map();

    options
      .slice()
      .sort((a, b) => Number(a?.position || 0) - Number(b?.position || 0))
      .forEach((opt) => {
        const date = String(opt?.date || "").trim();
        if (!date) return;
        const parsedDate = parseIsoDate(date);
        const defaults = getDefaultYearMonth();
        let day = byDate.get(date);
        if (!day) {
          day = {
            id: `${date}-${Math.random().toString(36).slice(2, 7)}`,
            year: parsedDate?.year || defaults.year,
            month: parsedDate?.month || defaults.month,
            day: parsedDate?.day || "",
            intervals: [],
          };
          byDate.set(date, day);
          grouped.push(day);
        }

        const start = normalizeTime(opt?.start_time);
        const end = normalizeTime(opt?.end_time);
        if (!start || !end) return;

        if (day.intervals.length >= MAX_INTERVALS_PER_DATE) return;
        if (day.intervals.some((it) => it.start === start && it.end === end)) return;
        day.intervals.push({ start, end });
      });

    const preparedDays = grouped.slice(0, MAX_DATES).map((d) => ({
      ...d,
      intervals: Array.isArray(d.intervals) ? d.intervals : [],
    }));
    return preparedDays.length ? preparedDays : [makeDay()];
  }, []);

  const loadPoll = React.useCallback(async () => {
    if (!eventId) {
      setPollLoaded(true);
      return;
    }
    setLoading(true);
    setPollLoaded(false);
    setLocalError("");
    try {
      const { data } = await api.get(`/events/${eventId}/concept-date-poll/`);
      const pollPayload = data?.poll || null;
      setHasExistingPoll(!!pollPayload);
      setDays(requireStartForDuration(
        failedPollDrafts.get(String(eventId)) || mapPollToDays(pollPayload),
        autoDurationMinutes
      ));
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        t("events.details.generic_error");
      setLocalError(msg);
      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message: msg, status } })
      );
      setDays(requireStartForDuration(
        failedPollDrafts.get(String(eventId)) || [makeDay()],
        autoDurationMinutes
      ));
      setHasExistingPoll(false);
    } finally {
      setLoading(false);
      setPollLoaded(true);
    }
  }, [autoDurationMinutes, eventId, mapPollToDays, t]);

  React.useEffect(() => {
    if (!visible || !eventId) return;
    void loadPoll();
  }, [eventId, loadPoll, visible]);

  React.useEffect(() => {
    if (visible) return;
    setLoading(false);
    setPollLoaded(false);
    setSubmitting(false);
    setLocalError("");
    setDays([makeDay()]);
    setHasExistingPoll(false);
  }, [visible]);

  React.useEffect(() => {
    if (!visible || !eventId) return undefined;
    const key = String(eventId);
    setSubmitting(pendingPollSaves.has(key));
    const handleSaveState = (event) => {
      if (event.detail?.eventId !== key) return;
      setSubmitting(event.detail.pending);
      if (!event.detail.pending) void loadPoll();
    };
    window.addEventListener(POLL_SAVE_STATE_EVENT, handleSaveState);
    return () => window.removeEventListener(POLL_SAVE_STATE_EVENT, handleSaveState);
  }, [eventId, loadPoll, visible]);

  const validationError = React.useMemo(() => {
    if (!Array.isArray(days) || days.length === 0) {
      return t("events.details.more_actions.date_poll_modal.validation_empty");
    }
    if (days.length > MAX_DATES) {
      return t("events.details.more_actions.date_poll_modal.validation_dates_limit", {
        count: MAX_DATES,
      });
    }

    const seenDates = new Set();
    let optionsCount = 0;
    for (const day of days) {
      const dateIso = toIsoDate(day);
      if (!dateIso) {
        return t("events.details.more_actions.date_poll_modal.validation_date_required");
      }
      if (!isFutureDate(dateIso)) {
        return t("events.details.more_actions.date_poll_modal.validation_date_future");
      }
      if (seenDates.has(dateIso)) {
        return t("events.details.more_actions.date_poll_modal.validation_duplicate_date");
      }
      seenDates.add(dateIso);

      const intervals = Array.isArray(day?.intervals) ? day.intervals : [];
      if (intervals.length > MAX_INTERVALS_PER_DATE) {
        return t("events.details.more_actions.date_poll_modal.validation_intervals_limit", {
          count: MAX_INTERVALS_PER_DATE,
        });
      }
      if (intervals.length === 0) {
        if (autoDurationMinutes) {
          return t("events.details.more_actions.date_poll_modal.validation_interval_required");
        }
        optionsCount += 1;
      } else {
        optionsCount += intervals.length;
      }

      for (const interval of intervals) {
        const start = normalizeTime(interval?.start);
        const end = autoDurationMinutes
          ? endTimeFromDuration(start, autoDurationMinutes)
          : normalizeTime(interval?.end);
        if (!start || !end) {
          if (autoDurationMinutes && start) {
            return t("events.details.more_actions.date_poll_modal.validation_duration_overflow");
          }
          return t("events.details.more_actions.date_poll_modal.validation_interval_required");
        }
        if (start >= end) {
          return t("events.details.more_actions.date_poll_modal.validation_interval_range");
        }
      }
    }

    if (optionsCount > MAX_OPTIONS) {
      return t("events.details.more_actions.date_poll_modal.validation_options_limit", {
        count: MAX_OPTIONS,
      });
    }

    return "";
  }, [autoDurationMinutes, days, t]);

  const canSubmit =
    visible &&
    !loading &&
    pollLoaded &&
    !submitting &&
    !pendingPollSaves.has(String(eventId)) &&
    !validationError &&
    Boolean(eventId);

  const addDate = () => {
    setDays((prev) => {
      if ((prev?.length || 0) >= MAX_DATES) return prev;
      return [...prev, ...requireStartForDuration([makeDay()], autoDurationMinutes)];
    });
  };

  const removeDate = (dayId) => {
    setDays((prev) => {
      const next = (prev || []).filter((d) => d.id !== dayId);
      return next.length ? next : requireStartForDuration([makeDay()], autoDurationMinutes);
    });
  };

  const updateDate = (dayId, value) => {
    const parsed = parseIsoDate(value);
    if (!parsed) return;
    setDays((prev) =>
      (prev || []).map((day) =>
        day.id === dayId ? { ...day, ...parsed } : day
      )
    );
  };

  const addInterval = (dayId) => {
    setDays((prev) =>
      (prev || []).map((d) => {
        if (d.id !== dayId) return d;
        const current = Array.isArray(d.intervals) ? d.intervals : [];
        if (current.length >= MAX_INTERVALS_PER_DATE) return d;
        return {
          ...d,
          intervals: [...current, { start: "", end: "" }],
        };
      })
    );
  };

  const removeInterval = (dayId, index) => {
    setDays((prev) =>
      (prev || []).map((d) => {
        if (d.id !== dayId) return d;
        const current = Array.isArray(d.intervals) ? d.intervals : [];
        if (autoDurationMinutes && current.length <= 1) return d;
        return {
          ...d,
          intervals: current.filter((_, idx) => idx !== index),
        };
      })
    );
  };

  const updateInterval = (dayId, index, field, value) => {
    setDays((prev) =>
      (prev || []).map((d) => {
        if (d.id !== dayId) return d;
        const current = Array.isArray(d.intervals) ? d.intervals : [];
        return {
          ...d,
          intervals: current.map((it, idx) =>
            idx === index ? { ...it, [field]: normalizeTime(value) } : it
          ),
        };
      })
    );
  };

  const handleSubmit = () => {
    if (!canSubmit || !eventId || pendingPollSaves.has(String(eventId))) {
      if (validationError) setLocalError(validationError);
      return;
    }

    const payload = {
      days: (days || []).map((day) => ({
        date: toIsoDate(day),
        intervals: (Array.isArray(day.intervals) ? day.intervals : [])
          .filter((it) => normalizeTime(it?.start) && (autoDurationMinutes || normalizeTime(it?.end)))
          .map((it) => ({
            start: normalizeTime(it.start),
            end: autoDurationMinutes
              ? endTimeFromDuration(normalizeTime(it.start), autoDurationMinutes)
              : normalizeTime(it.end),
          })),
      })),
    };

    const savedExistingPoll = hasExistingPoll;
    updatePendingPollSave(eventId, true);
    setLocalError("");
    const save = api.post(`/events/${eventId}/concept-date-poll/`, payload);
    onClose();
    window.dispatchEvent(new CustomEvent("global-info", {
      detail: { message: t("events.details.more_actions.date_poll_modal.sending_background") },
    }));

    void save.then(({ data }) => {
      failedPollDrafts.delete(String(eventId));
      window.dispatchEvent(new CustomEvent("global-info", {
        detail: {
          message: savedExistingPoll
            ? t("events.details.more_actions.date_poll_modal.updated")
            : t("events.details.more_actions.date_poll_modal.created"),
        },
      }));
      if (typeof onSaved === "function") {
        Promise.resolve().then(() => onSaved(data?.poll || null)).catch(console.error);
      }
    }).catch((err) => {
      failedPollDrafts.set(String(eventId), days);
      const status = err?.response?.status;
      const reason = err?.response?.data?.error || err?.message || t("events.details.generic_error");
      window.dispatchEvent(new CustomEvent("global-error", {
        detail: {
          message: `${t("events.details.more_actions.date_poll_modal.send_failed")} ${reason}`,
          status,
        },
      }));
    }).finally(() => updatePendingPollSave(eventId, false));
  };

  if (!visible) return null;

  const title = hasExistingPoll
    ? t("events.details.more_actions.date_poll_modal.title_edit")
    : t("events.details.more_actions.date_poll_modal.title_create");
  const submitLabel = submitting
    ? t("events.details.more_actions.date_poll_modal.saving")
    : hasExistingPoll
    ? t("events.details.more_actions.date_poll_modal.save_edit")
    : t("events.details.more_actions.date_poll_modal.send");
  const submitButton = (
    <AppModalButton
      onClick={handleSubmit}
      disabled={!canSubmit}
      loading={submitting}
      className="!bg-[#3e6ffb] !text-white disabled:!bg-white/12 disabled:!text-white/40 disabled:!shadow-none"
      data-testid="concept-date-poll-submit"
    >
      {submitLabel}
    </AppModalButton>
  );
  const embeddedSubmitButton = (
    <button
      type="button"
      onClick={handleSubmit}
      disabled={!canSubmit}
      aria-busy={submitting}
      className="visible block h-12 w-full shrink-0 rounded-full bg-[#3e6ffb] px-5 text-[16px] font-semibold leading-none text-white opacity-100 shadow-[0_10px_28px_rgba(62,111,251,0.24)] transition active:scale-[0.985] disabled:cursor-not-allowed disabled:bg-white/12 disabled:text-white/40 disabled:shadow-none"
      data-testid="concept-date-poll-submit"
      data-embedded-submit="true"
    >
      {submitLabel}
    </button>
  );
  const errorContent = validationError || localError ? (
    <div className="mb-2" role="alert">
      {validationError ? (
        <p className="text-sm text-red-200">{validationError}</p>
      ) : null}
      {localError ? (
        <p className="text-sm text-red-200">{localError}</p>
      ) : null}
    </div>
  ) : null;
  const footerContent = (
    <div>
      {errorContent}
      <div className="flex gap-3">
        <AppModalButton
          variant="secondary"
          onClick={onClose}
          disabled={submitting}
          className="flex-1"
        >
          {t("events.details.more_actions.date_poll_modal.close")}
        </AppModalButton>
        <div className="flex-1">
          {submitButton}
        </div>
      </div>
    </div>
  );
  const embeddedFooterContent = (
    <div className="relative z-30 min-h-12 w-full opacity-100">
      {errorContent}
      {embeddedSubmitButton}
    </div>
  );

  const bodyContent = (
    <>
      {!loading ? (
        <div className="space-y-3">
          {(days || []).map((day, dayIndex) => (
            <div key={day.id} className="min-w-0 overflow-hidden rounded-[18px] border border-white/10 bg-white/[0.07] p-3">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-white/90">
                  {t("events.details.more_actions.date_poll_modal.date_label", {
                    index: dayIndex + 1,
                  })}
                </p>
                {(days || []).length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeDate(day.id)}
                    className="ml-auto text-xs font-semibold text-red-100"
                  >
                    {t("events.details.more_actions.date_poll_modal.remove_date")}
                  </button>
                ) : null}
              </div>

              <div className="relative mt-2 h-11 min-w-0 max-w-full overflow-hidden rounded-full border border-white/14 bg-white/10 focus-within:ring-2 focus-within:ring-white/40">
                <input
                  type="date"
                  min={getMinSelectableDate()}
                  value={toIsoDate(day)}
                  onChange={(e) => updateDate(day.id, e.target.value)}
                  aria-label={t("events.details.more_actions.date_poll_modal.date_label", {
                    index: dayIndex + 1,
                  })}
                  className={`absolute inset-0 z-10 block h-full w-full min-w-0 max-w-full cursor-pointer appearance-none rounded-full border-0 bg-transparent px-4 font-semibold outline-none [color-scheme:dark] [inline-size:100%] [min-inline-size:0] ${
                    toIsoDate(day) ? "opacity-0" : "text-white"
                  }`}
                />
                {toIsoDate(day) ? (
                  <span
                    data-testid={`date-display-${dayIndex + 1}`}
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 flex items-center justify-center px-4 text-center font-semibold leading-none text-white"
                  >
                    {formatDateForDisplay(
                      toIsoDate(day),
                      i18n?.resolvedLanguage || i18n?.language
                    )}
                  </span>
                ) : null}
              </div>

              <div className="mt-3 space-y-2">
                {(Array.isArray(day.intervals) ? day.intervals : []).map((interval, idx) => (
                  <div
                    key={`${day.id}-interval-${idx}`}
                    className={`grid min-w-0 items-end gap-2 ${autoDurationMinutes && day.intervals.length === 1 ? "grid-cols-2" : "grid-cols-[minmax(0,1fr)_minmax(0,1fr)_40px]"}`}
                    data-testid={`concept-date-poll-interval-${dayIndex}-${idx}`}
                  >
                    <div
                      className="contents"
                      data-testid={`concept-date-poll-time-grid-${dayIndex}-${idx}`}
                    >
                      <label className="block min-w-0 overflow-hidden">
                        <span className="mb-1 block px-2 text-xs font-semibold text-white/75">
                          {t("events.details.more_actions.date_poll_modal.start_time_label")}
                        </span>
                        <div className="relative h-10 min-w-0 max-w-full overflow-hidden rounded-full border border-white/14 bg-white/10 focus-within:ring-2 focus-within:ring-inset focus-within:ring-white/40">
                          <input
                            type="time"
                            value={normalizeTime(interval?.start)}
                            onChange={(e) => updateInterval(day.id, idx, "start", e.target.value)}
                            aria-label={t(
                              "events.details.more_actions.date_poll_modal.start_time_aria",
                              { date: dayIndex + 1, interval: idx + 1 }
                            )}
                            className="block h-full w-full min-w-0 max-w-full cursor-pointer appearance-none rounded-full border-0 bg-transparent px-1 text-center font-semibold text-white outline-none [color-scheme:dark] [inline-size:100%] [min-inline-size:0] [&::-webkit-datetime-edit]:mx-auto [&::-webkit-datetime-edit-fields-wrapper]:mx-auto [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-1"
                          />
                        </div>
                      </label>
                      <label className="block min-w-0 overflow-hidden">
                        <span className="mb-1 block px-2 text-xs font-semibold text-white/75">
                          {t("events.details.more_actions.date_poll_modal.end_time_label")}
                        </span>
                        {autoDurationMinutes ? (
                          <div className="flex h-10 min-w-0 items-center justify-center rounded-full border border-white/14 bg-white/10 px-1 text-center font-semibold text-white/85" aria-label={t("events.details.more_actions.date_poll_modal.end_time_label")}>
                            {endTimeFromDuration(normalizeTime(interval?.start), autoDurationMinutes) || "—"}
                          </div>
                        ) : (
                          <div className="relative h-10 min-w-0 max-w-full overflow-hidden rounded-full border border-white/14 bg-white/10 focus-within:ring-2 focus-within:ring-inset focus-within:ring-white/40">
                            <input
                              type="time"
                              value={normalizeTime(interval?.end)}
                              onChange={(e) => updateInterval(day.id, idx, "end", e.target.value)}
                              aria-label={t(
                                "events.details.more_actions.date_poll_modal.end_time_aria",
                                { date: dayIndex + 1, interval: idx + 1 }
                              )}
                              className="block h-full w-full min-w-0 max-w-full cursor-pointer appearance-none rounded-full border-0 bg-transparent px-1 text-center font-semibold text-white outline-none [color-scheme:dark] [inline-size:100%] [min-inline-size:0] [&::-webkit-datetime-edit]:mx-auto [&::-webkit-datetime-edit-fields-wrapper]:mx-auto [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-1"
                            />
                          </div>
                        )}
                      </label>
                    </div>
                    {(!autoDurationMinutes || day.intervals.length > 1) && <AppModalIconButton
                      onClick={() => removeInterval(day.id, idx)}
                      aria-label={t("events.details.more_actions.date_poll_modal.remove_interval_aria")}
                      title={t("events.details.more_actions.date_poll_modal.remove_interval")}
                      className="h-10 w-10 self-end bg-white/10"
                    >
                      <AppModalCloseIcon />
                    </AppModalIconButton>}
                  </div>
                ))}
              </div>

              {(Array.isArray(day.intervals) ? day.intervals.length : 0) < MAX_INTERVALS_PER_DATE ? (
                <button
                  type="button"
                  onClick={() => addInterval(day.id)}
                  className="mt-3 h-10 rounded-full border border-white/14 bg-white/10 px-4 text-sm font-semibold text-white"
                >
                  {t("events.details.more_actions.date_poll_modal.add_interval")}
                </button>
              ) : null}

              {(Array.isArray(day.intervals) ? day.intervals.length : 0) === 0 ? (
                <p className="mt-2 text-xs text-white/80">
                  {t("events.details.more_actions.date_poll_modal.whole_day_hint")}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {(days || []).length < MAX_DATES && !loading ? (
        <button
          type="button"
          onClick={addDate}
          className="mt-4 h-11 w-full rounded-full border border-white/14 bg-white/10 font-semibold text-white"
        >
          {t("events.details.more_actions.date_poll_modal.add_date")}
        </button>
      ) : null}

    </>
  );

  if (embedded) {
    return (
      <div
        className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        data-testid="concept-date-poll-embedded"
      >
        <div
          className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain pr-1"
          data-testid="concept-date-poll-scroll-region"
        >
          <EventToolsAnimatedState
            loading={loading || !pollLoaded}
            skeleton={<EventToolsSkeleton variant="datePoll" />}
          >
            {bodyContent}
          </EventToolsAnimatedState>
        </div>
        <div
          className="relative z-20 shrink-0 border-t border-white/10 pt-3"
          data-testid="concept-date-poll-footer"
        >
          {embeddedFooterContent}
        </div>
      </div>
    );
  }

  return (
    <AppModalShell
      open={visible}
      title={title}
      onClose={onClose}
      closeAriaLabel={t("events.details.more_actions.date_poll_modal.close")}
      zIndexClassName={zIndexClassName}
      maxWidthClassName="max-w-[720px]"
      descriptionClassName="text-center"
      leftAction={
        typeof onBack === "function" ? (
          <EventToolsBackButton
            onClick={onBack}
            label={t("events.details.more_actions.back")}
            disabled={submitting}
          />
        ) : null
      }
      footer={footerContent}
    >
      <EventToolsAnimatedState
        loading={loading || !pollLoaded}
        skeleton={<EventToolsSkeleton variant="datePoll" />}
      >
        {bodyContent}
      </EventToolsAnimatedState>
    </AppModalShell>
  );
};

export default ConceptDatePollModal;
